import { useCallback, useEffect, useState } from 'react'
import {
  ACTION_HUB_OVERLAY_BODY_CLASSES,
  isSalesforceChatVisible,
  subscribeSalesforceChatVisibility,
} from './actionHubHelpers'

const { PEARY } = ACTION_HUB_OVERLAY_BODY_CLASSES

const isPearyChatOpen = () => document.body.classList.contains(PEARY)

/** Poll until the SF chat window appears or we give up (~6s). */
const SF_CHAT_VISIBILITY_POLL_INTERVAL_MS = 250
const SF_CHAT_VISIBILITY_MAX_POLL_ATTEMPTS = 24

export const useActionHubOverlayHidden = (feedbackOpen = false) => {
  const [sfChatOpen, setSfChatOpen] = useState(false)
  const [pearyOpen, setPearyOpen] = useState(isPearyChatOpen)

  useEffect(() => {
    const unsubscribeSf = subscribeSalesforceChatVisibility({
      onOpen: () => setSfChatOpen(true),
      onClose: () => setSfChatOpen(false),
    })

    const syncPeary = () => setPearyOpen(isPearyChatOpen())
    const pearyObserver = new MutationObserver(syncPeary)
    pearyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => {
      unsubscribeSf()
      pearyObserver.disconnect()
    }
  }, [])

  const openSfChat = useCallback(() => {
    // Hide the Action Hub immediately; SF chat can take a moment to render.
    setSfChatOpen(true)

    let pollAttempts = 0
    const pollId = window.setInterval(() => {
      pollAttempts += 1
      if (isSalesforceChatVisible()) {
        setSfChatOpen(true)
        window.clearInterval(pollId)
      } else if (pollAttempts >= SF_CHAT_VISIBILITY_MAX_POLL_ATTEMPTS) {
        // Chat never appeared — restore the Action Hub so the user isn't stuck.
        setSfChatOpen(false)
        window.clearInterval(pollId)
      }
    }, SF_CHAT_VISIBILITY_POLL_INTERVAL_MS)
  }, [])

  const closeSfChat = useCallback(() => {
    setSfChatOpen(false)
  }, [])

  return {
    hidden: feedbackOpen || sfChatOpen || pearyOpen,
    openSfChat,
    closeSfChat,
  }
}

export default useActionHubOverlayHidden
